import Button from '@atoms/Button/Button';
import CheckboxInput from '@atoms/Inputs/CheckboxInput';
import {
  CATERING_INDUSTRY_FUNCTION_CATEGORY_CODE,
  FACILITY_FUNCTION_CATEGORY_CODE,
  MANAGEMENT_AND_BOARD_FUNCTION_CATEGORY_CODE,
  STAFF_AND_ADMINISTRATION_FUNCTION_CATEGORY_CODE,
  VOLUNTEER_APPLICATION_CODE,
} from '@constants';
import { vacancyFiltersType } from '@lib/proptypes';
import MatchMakerQuestion from '@molecules/MatchMaker/MatchMakerQuestion';
import { useMatchmakerQuestion } from '@utils';
import useApplicationFormsAnswer from '@utils/matchmaker/useApplicationFormsAnswer';
import useFunctionCategoriesAnswer from '@utils/matchmaker/useFunctionCategoriesAnswer';
import PropTypes from 'prop-types';

import styles from './Matchmaker.module.scss';

const filterKey = 'hours';

const options = [
  { min: 1, max: 8 },
  { min: 9, max: 16 },
  { min: 17, max: 24 },
  { min: 25, max: 32 },
  { min: 33, max: 36 },
];

export default function Question5({ handleNavigation, filters }) {
  const { selectedApplicationFormCode } = useApplicationFormsAnswer(filters);
  const { selectedFunctionCategoryCode } = useFunctionCategoriesAnswer();

  const { inputs, handleChange, handlePreviousUrl, handleNextUrl } =
    useMatchmakerQuestion({
      key: filterKey,
      callback: handleNavigation,
      optional: true,
      initialState: state => {
        if (!state.minHours || !state.maxHours) return [];

        return options
          .filter(
            option =>
              option.min >= +state.minHours && option.max <= +state.maxHours
          )
          .map(option => `${option.min}-${option.max}`);
      },
      saveStateAs: () => {
        const mappedInputs = inputs[filterKey].map(input =>
          input.split('-').map(value => +value)
        );

        if (!mappedInputs.length)
          return { minHours: undefined, maxHours: undefined };

        const allMinHours = mappedInputs.map(input => input[0]);
        const allMaxHours = mappedInputs.map(input => input[1]);

        return {
          minHours: Math.min(...allMinHours),
          maxHours: Math.max(...allMaxHours),
        };
      },
    });

  const shouldSkipPreviousQuestion = () => {
    // if the selected application form is volunteer, skip the previous question
    if (selectedApplicationFormCode === VOLUNTEER_APPLICATION_CODE) {
      return true;
    }
    return [
      FACILITY_FUNCTION_CATEGORY_CODE,
      CATERING_INDUSTRY_FUNCTION_CATEGORY_CODE,
      STAFF_AND_ADMINISTRATION_FUNCTION_CATEGORY_CODE,
      MANAGEMENT_AND_BOARD_FUNCTION_CATEGORY_CODE,
    ].includes(selectedFunctionCategoryCode);
  };

  const getPreviousLink = () =>
    shouldSkipPreviousQuestion() ? '/matchmaker/3' : '/matchmaker/4';

  const shouldSkipNextQuestion = () =>
    [
      STAFF_AND_ADMINISTRATION_FUNCTION_CATEGORY_CODE,
      MANAGEMENT_AND_BOARD_FUNCTION_CATEGORY_CODE,
    ].includes(selectedFunctionCategoryCode);

  const getNextLink = () =>
    shouldSkipNextQuestion() ? '/matchmaker/7' : '/matchmaker/6';

  return (
    <MatchMakerQuestion
      number={5}
      total={7}
      question="Hoeveel uur wil je werken?"
      extraClasses={styles.matchMakerQuestion}
    >
      <div className={styles.inputsContainer}>
        {options.map(option => {
          const value = `${option.min}-${option.max}`;
          return (
            <CheckboxInput
              key={`hours-${value}`}
              id={value}
              value={value}
              name={filterKey}
              onChange={handleChange}
              label={`${option.min} - ${option.max}`}
              checked={inputs[filterKey]?.includes(value)}
              groupCheckbox
            />
          );
        })}
      </div>

      <p className={styles.choiceHelp}>Je mag meerdere opties aanvinken</p>

      <div className={styles.ctaContainer}>
        <Button
          key="matchmaker-back-button"
          label="Terug"
          href={getPreviousLink()}
          size="smallMobile"
          onClick={handlePreviousUrl}
          color="secondary"
        />
        <Button
          key="matchmaker-next-button"
          label="Volgende"
          href={getNextLink()}
          size="smallMobile"
          onClick={handleNextUrl}
          disabled={!inputs.hours.length}
        />
      </div>
    </MatchMakerQuestion>
  );
}

Question5.propTypes = {
  filters: vacancyFiltersType,
  handleNavigation: PropTypes.func,
};
